import {
  useCreateCbtTestMutation,
  useUpdateCbtTestMutation,
  useDeleteCbtTestMutation,
  useStartCbtTestMutation,
  useSubmitAnswersMutation,
  useSubmitCbtTestMutation,
  useDeleteCbtTestSessionMutation,
  ListCbtTestsDocument,
} from '@/types/vue-apollo-helpers';
import { useToast } from 'primevue/usetoast';
import { useCBTSessionStore } from '@/store/cbtSession';
import type { CreateCbtTestInput, UpdateCbtTestInput, SubmitCbtTestInput, StudentAnswerInput, MutationSubmitAnswersArgs } from '@/types/composition-functions';

export function useCbtTestMutations() {
  const toast = useToast();
  const sessionStore = useCBTSessionStore();

  const { mutate: createCBTTestMutate, loading: creating } = useCreateCbtTestMutation({
    refetchQueries: [{ query: ListCbtTestsDocument }],
  });
  const { mutate: updateCBTTestMutate, loading: updating } = useUpdateCbtTestMutation({
    refetchQueries: [{ query: ListCbtTestsDocument }],
  });
  const { mutate: deleteCBTTestMutate, loading: deleting } = useDeleteCbtTestMutation({
    refetchQueries: [{ query: ListCbtTestsDocument }],
  });
  const { mutate: startCBTTestMutate, loading: starting } = useStartCbtTestMutation();
  const { mutate: submitAnswersMutate } = useSubmitAnswersMutation();
  const { mutate: submitCBTMutate, loading: submitting } = useSubmitCbtTestMutation();
  const { mutate: deleteSessionMutate, loading: deletingSession } = useDeleteCbtTestSessionMutation();

  async function createCBTTest(input: CreateCbtTestInput) {
    try {
      const { data } = (await createCBTTestMutate({ input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'CBT Test created successfully!', life: 3000 });
      return data?.createCBTTest;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to create CBT Test', life: 3000 });
      throw error;
    }
  }

  async function updateCBTTest(id: string, input: UpdateCbtTestInput) {
    try {
      const { data } = (await updateCBTTestMutate({ id, input }))!;
      toast.add({ severity: 'success', summary: 'Success', detail: 'CBT Test updated successfully!', life: 3000 });
      return data?.updateCBTTest;
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to update CBT Test', life: 3000 });
      throw error;
    }
  }

  async function deleteCBTTest(id: string) {
    try {
      const { data } = (await deleteCBTTestMutate({ id }))!;
      if (data?.deleteCBTTest?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: data.deleteCBTTest.message || 'CBT Test deleted', life: 3000 });
        return true;
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: data?.deleteCBTTest?.message || 'Failed to delete CBT Test', life: 3000 });
        return false;
      }
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Error deleting CBT Test', life: 3000 });
      throw error;
    }
  }

  async function startCBTTest(testId: string) {
    try {
      const { data } = (await startCBTTestMutate({ testId }))!;
      if (data?.startCBTTest) {
        sessionStore.setSession(data.startCBTTest);
        toast.add({ severity: 'success', summary: 'Success', detail: 'CBT Test started!', life: 3000 });
        return data.startCBTTest;
      }
      toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to start CBT Test', life: 3000 });
      return null;
    } catch (error) {
      const gqlError = error as { message?: string; graphQLErrors?: Array<{ message?: string; extensions?: { validation?: Record<string, string[]> } }> };
      if (gqlError.message?.includes('already completed') ||
                gqlError.graphQLErrors?.[0]?.message?.includes('already completed')) {
        toast.add({
          severity: 'warn',
          summary: 'Assessment Completed',
          detail: 'You have already completed this assessment.',
          life: 5000,
        });
        return null;
      }

      let errorMessage = 'Error starting CBT Test';
      const validationErrors = gqlError.graphQLErrors?.[0]?.extensions?.validation;
      if (validationErrors) {
        const firstKey = Object.keys(validationErrors)[0];
        if (firstKey && validationErrors[firstKey]?.[0]) {
          errorMessage = validationErrors[firstKey][0];
        }
      } else if (gqlError.message && !gqlError.message.includes('Validation failed')) {
        errorMessage = gqlError.message;
      }

      toast.add({ severity: 'error', summary: 'Error', detail: errorMessage, life: 5000 });
      throw error;
    }
  }

  async function submitAnswers(sessionId: string, answers: StudentAnswerInput[]) {
    try {
      const variables: MutationSubmitAnswersArgs = {
        sessionId,
        answers,
      };
      const { data } = (await submitAnswersMutate(variables))!;

      if (data?.submitAnswers) {
        return data.submitAnswers;
      }
    } catch (error) {
      return null;
    }
  }

  /**
   * Submit a student's exam. Failures are rethrown without a toast: the exam page
   * shows a single, student-friendly message so it isn't doubled up with raw
   * transport text.
   */
  async function submitCBTTest(input: SubmitCbtTestInput) {
    const { data } = (await submitCBTMutate({ input }))!;
    if (data?.submitCBTTest) {
      toast.add({ severity: 'success', summary: 'Success', detail: 'CBT Test Submitted Successfully', life: 3000 });
      return data.submitCBTTest;
    }
    throw new Error('The exam submission returned no result.');
  }

  async function deleteCBTTestSession(id: string) {
    try {
      const { data } = (await deleteSessionMutate({ id }))!;
      if (data?.deleteCBTTestSession?.success) {
        toast.add({ severity: 'success', summary: 'Success', detail: data.deleteCBTTestSession.message || 'Session deleted', life: 3000 });
        return true;
      } else {
        toast.add({ severity: 'error', summary: 'Error', detail: data?.deleteCBTTestSession?.message || 'Failed to delete session', life: 3000 });
        return false;
      }
    } catch (error) {
      toast.add({ severity: 'error', summary: 'Error', detail: 'Error deleting session', life: 3000 });
      throw error;
    }
  }

  return {
    createCBTTest,
    updateCBTTest,
    deleteCBTTest,
    startCBTTest,
    submitAnswers,
    submitCBTTest,
    deleteCBTTestSession,
    creating,
    updating,
    deleting,
    starting,
    submitting,
    deletingSession,
  };
}
